/**
 * CardDeviceTemplate Component
 * 
 * Displays a device enum template card for selection during device registration.
 * Shows device information and allows user to select it as a template for
 * registering a new physical device instance.
 * 
 * @param {Object} deviceEnum - The device enum template data
 * @param {Function} onSelect - Callback function when template is selected
 * @param {boolean} isSelected - Whether this template is currently selected
 */
import React from "react";
import {
  Paper,
  Box,
  Typography,
  Chip,
  Tooltip
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SensorsIcon from "@mui/icons-material/Sensors";
import SettingsInputComponentIcon from "@mui/icons-material/SettingsInputComponent";
import { ImageLoader } from "../../../../components/ui/img/ImageLoader";

interface CardDeviceTemplateProps {
  deviceEnum: any;
  onSelect: (deviceEnum: any) => void;
  isSelected: boolean;
}

function CardDeviceTemplate({ deviceEnum, onSelect, isSelected }: CardDeviceTemplateProps) {
  return (
    <Paper
      sx={{
        width: "100%",
        padding: "16px",
        cursor: "pointer",
        transition: "all 0.2s ease-in-out",
        border: isSelected ? "2px solid #44a99a" : "1px solid #e0e0e0",
        backgroundColor: isSelected ? "#f0faf8" : "#fff",
        "&:hover": {
          boxShadow: "0 4px 12px rgba(68, 169, 154, 0.2)",
          transform: "translateY(-2px)"
        }
      }}
      onClick={() => onSelect(deviceEnum)}
      elevation={isSelected ? 3 : 1}
    >
      <Box display="flex" gap={2}>
        {/* Device Icon */}
        <Box 
          sx={{ 
            width: "80px", 
            height: "80px", 
            flexShrink: 0,
            borderRadius: "8px",
            overflow: "hidden",
            border: "1px solid #e0e0e0"
          }}
        >
          <ImageLoader
            imgUrl={deviceEnum.icon}
            radiusStyle={"8px"}
          />
        </Box>

        {/* Device Info */}
        <Box flex={1}>
          <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
            <Box>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 600, 
                  color: "#39393c"
                }}
              >
                {deviceEnum.name}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontStyle: "italic", 
                  color: "#666"
                }}
              >
                {deviceEnum.type}
              </Typography>
            </Box>
            {isSelected && (
              <CheckCircleIcon sx={{ color: "#44a99a" }} />
            )}
          </Box>

          {/* Badges */}
          <Box display="flex" gap={0.5} flexWrap="wrap" mb={1}>
            <Chip 
              label={deviceEnum.os} 
              size="small" 
              sx={{ 
                backgroundColor: "#44a99a",
                color: "#fff",
                fontSize: "0.7rem"
              }}
            />
          </Box>

          {/* Quick Stats */}
          <Box display="flex" gap={2} mt={1}>
            {deviceEnum.sensors && deviceEnum.sensors.length > 0 && (
              <Tooltip title={`${deviceEnum.sensors.length} sensors`}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <SensorsIcon sx={{ fontSize: "0.9rem", color: "#666" }} />
                  <Typography variant="caption" color="#666">
                    {deviceEnum.sensors.length}
                  </Typography>
                </Box>
              </Tooltip>
            )}
            {deviceEnum.io && deviceEnum.io.length > 0 && (
              <Tooltip title={`${deviceEnum.io.length} I/O`}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <SettingsInputComponentIcon sx={{ fontSize: "0.9rem", color: "#666" }} />
                  <Typography variant="caption" color="#666">
                    {deviceEnum.io.length}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}

export default CardDeviceTemplate;
